import {
  ANALYTICS_PERIODS,
  type AnalyticsPeriod,
  type ChildId,
  type ChildWithProfile,
  type ContentItem,
  type Locale,
} from '@kidshub/contracts';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { collectionFelt, LOOSE_ITEM_FELT } from '../catalog/collections-rail.js';
import { splitDuration } from '../lib/duration.js';
import { Icon, Segmented } from '../ui/kit.js';

/**
 * Which child and which period the watching screens are about.
 *
 * In the address rather than in state, so that the overview and a video's page
 * agree without passing anything, a refresh keeps the view, and a link to a
 * video carries the period it was opened from. A week by default: the owner's
 * choice on the board (2026-10-09).
 */
export const DEFAULT_PERIOD: AnalyticsPeriod = 7;

export const useWatchingScope = (familyChildren: ChildWithProfile[]) => {
  const [params, setParams] = useSearchParams();

  const requested = params.get('child');
  const child =
    familyChildren.find((entry) => entry.child.id === requested) ?? familyChildren[0] ?? null;
  const asked = Number(params.get('days'));
  const days: AnalyticsPeriod =
    ANALYTICS_PERIODS.find((period) => period === asked) ?? DEFAULT_PERIOD;

  const update = (patch: { child?: ChildId; days?: AnalyticsPeriod }) => {
    const next = new URLSearchParams(params);
    if (patch.child !== undefined) {
      next.set('child', patch.child);
    }
    if (patch.days !== undefined) {
      next.set('days', String(patch.days));
    }
    setParams(next, { replace: true });
  };

  return {
    child,
    days,
    /** The scope, to carry on a link from one watching screen to the other. */
    search: `?${new URLSearchParams({
      ...(child === null ? {} : { child: child.child.id }),
      days: String(days),
    }).toString()}`,
    setChild: (childId: ChildId) => update({ child: childId }),
    setDays: (period: AnalyticsPeriod) => update({ days: period }),
  };
};

/** The period switch, shared by both screens. */
export const PeriodSwitch = ({
  days,
  onChange,
}: {
  days: AnalyticsPeriod;
  onChange: (days: AnalyticsPeriod) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Segmented<`${AnalyticsPeriod}`>
      label={t('watching.period')}
      value={`${days}`}
      options={ANALYTICS_PERIODS.map((period) => ({
        value: `${period}`,
        label: t(`watching.periods.${period}`),
      }))}
      onChange={(value) => onChange(Number(value) as AnalyticsPeriod)}
    />
  );
};

/** "1 sa 12 dk" or "12 dk", as a parent says it. */
export const useDurationText = () => {
  const { t } = useTranslation();
  return (seconds: number) => {
    const { hours, minutes } = splitDuration(seconds);
    return hours > 0
      ? t('watching.duration.hoursMinutes', { hours, minutes })
      : t('watching.duration.minutes', { minutes: Math.max(minutes, seconds > 0 ? 1 : 0) });
  };
};

/** A local day as the chart labels it: the weekday for a week, the date for a month. */
export const dayLabel = (day: string, today: string, locale: Locale, isLong: boolean): string => {
  if (day === today) {
    return '';
  }
  const date = new Date(`${day}T12:00:00Z`);
  return isLong
    ? String(date.getUTCDate())
    : new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date);
};

/** A small poster for a list row, on the item's own picture or its collection's felt. */
export const ItemThumb = ({ item, felt }: { item: ContentItem | undefined; felt: string }) => (
  <div className="poster" style={{ '--pc': felt } as CSSProperties} aria-hidden="true">
    {item?.posterUrl ? (
      <img className="lib-poster-img" src={item.posterUrl} alt="" loading="lazy" />
    ) : (
      <span className="lib-poster-mark">
        <Icon name="play" small />
      </span>
    )}
  </div>
);

/** The felt an item wears: its first collection's, as everywhere else in the panel. */
export const feltFor = (
  itemId: string,
  collections: { id: string; itemIds: string[] }[],
): string => {
  const home = collections.find((collection) => collection.itemIds.includes(itemId));
  return home === undefined ? LOOSE_ITEM_FELT : collectionFelt(home.id);
};
